/**
 * Servicio para facturar remisión y crear nota crédito en OFIMA (API intermedia).
 * Usado en el flujo de cancelación de pedido.
 */
import axiosInstance from '../../api/axiosInstance'

export interface FacturarBody {
  tipoFactura?: 'FS' | 'FR'
  sglOrderId?: string
  idMarket?: string
  prefijo?: string
  numeroPedido?: string
  usuario?: string
}

export interface NotaCreditoLinea {
  producto: string
  cantidad: number
  valorUnitario: number
}

export interface NotaCreditoBody {
  sglOrderId?: string
  idMarket?: string
  prefijo?: string
  numeroPedido?: string
  usuario?: string
  lineas?: NotaCreditoLinea[]
}

export interface FacturarResponseData {
  tipodctoRem: string
  nrodctoRem: string
  tipodctoFactura: string
  nrodctoFactura: string
  fechaFactura: string
  cantidadLineas: number
  pedido?: { idMarket: string; prefijo: string; numero: string }
}

export interface NotaCreditoResponseData {
  tipodctoRem: string
  nrodctoRem: string
  tipodctoNC: string
  nrodctoNC: string
  fechaNC: string
  cantidadLineas: number
  pedido?: { idMarket: string; prefijo: string; numero: string }
}

export interface OfimaApiError {
  success: false
  message: string
  error?: { number?: number; message?: string; procedure?: string; line?: number }
  remision?: { tipodcto: string; nrodcto: string }
  timestamp?: string
}

function getApiMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const res = (err as {
      response?: {
        data?: {
          message?: string
          error?: { number?: number; message?: string }
        }
      }
    }).response
    // Priorizar detalle de OFIMA cuando exista (más útil que el mensaje genérico).
    if (res?.data?.error?.message) return res.data.error.message
    if (res?.data?.message) return res.data.message
  }
  if (err instanceof Error) return err.message
  return 'Error desconocido'
}

/**
 * Factura una remisión en OFIMA (crea factura FS o FR).
 * POST /ofima-sync/remisiones/:tipodcto/:nrodcto/facturar
 */
export async function facturarRemision(
  tipodcto: string,
  nrodcto: string,
  body?: FacturarBody
): Promise<{ success: true; data: FacturarResponseData; message: string }> {
  try {
    const response = await axiosInstance.post<{
      success: boolean
      message: string
      data: FacturarResponseData
    }>(`/ofima-sync/remisiones/${tipodcto}/${nrodcto}/facturar`, body ?? {})
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Error al facturar remisión')
    }
    return {
      success: true,
      data: response.data.data,
      message: response.data.message
    }
  } catch (err) {
    throw new Error(getApiMessage(err))
  }
}

/**
 * Crea una nota crédito desde una remisión en OFIMA.
 * Sin lineas o lineas vacías = NC por 100% de la remisión.
 * POST /ofima-sync/remisiones/:tipodcto/:nrodcto/nota-credito
 */
export async function crearNotaCreditoRemision(
  tipodcto: string,
  nrodcto: string,
  body: NotaCreditoBody
): Promise<{ success: true; data: NotaCreditoResponseData; message: string }> {
  try {
    const response = await axiosInstance.post<{
      success: boolean
      message: string
      data: NotaCreditoResponseData
    }>(`/ofima-sync/remisiones/${tipodcto}/${nrodcto}/nota-credito`, body)
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Error al crear nota crédito')
    }
    return {
      success: true,
      data: response.data.data,
      message: response.data.message
    }
  } catch (err) {
    throw new Error(getApiMessage(err))
  }
}
